import type { AppVersion, UpdateAppVersionPayload } from '../../types/index.js';
import { baseApi } from '../base-api.js';

type AppVersionResponse = { app_version: AppVersion };
type AppVersionMutationResponse = { app_version: AppVersion; message: string };

/**
 * Mirrors `apps/server/src/modules/app-version` — a public read (`appVersionPublicController`,
 * `GET /app-version`, no auth) plus an admin read/write pair (`appVersionAdminController`,
 * `GET`/`PUT /admin/app-version`) that return/accept the exact same `AppVersion` shape. Same
 * split as `changelog.api.ts`'s public/admin pair. Both queries share the `AppVersion` tag's
 * single `SELF` id (there's only one row) so updating from the admin panel also invalidates
 * whatever the public read cached.
 */
export const appVersionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPublicAppVersion: builder.query<AppVersion, void>({
      query: () => ({ path: ['app-version'], method: 'get' }),
      transformResponse: (response: AppVersionResponse) => response.app_version,
      providesTags: [{ type: 'AppVersion', id: 'SELF' }],
    }),

    getAdminAppVersion: builder.query<AppVersion, void>({
      query: () => ({ path: ['admin', 'app-version'], method: 'get' }),
      transformResponse: (response: AppVersionResponse) => response.app_version,
      providesTags: [{ type: 'AppVersion', id: 'SELF' }],
    }),

    updateAppVersion: builder.mutation<AppVersion, UpdateAppVersionPayload>({
      query: (body) => ({ path: ['admin', 'app-version'], method: 'put', body }),
      transformResponse: (response: AppVersionMutationResponse) => response.app_version,
      invalidatesTags: [{ type: 'AppVersion', id: 'SELF' }],
    }),
  }),
});

export const { useGetPublicAppVersionQuery, useGetAdminAppVersionQuery, useUpdateAppVersionMutation } = appVersionApi;
